import React, { useEffect } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import {
  alpha,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';

import { PurchaseExtensionOnboardingDemo } from '@/components/Onboarding/PurchaseExtensionOnboardingDemo';

interface LoginNotificationModalProps {
  open: boolean;
  onClose: () => void;
  refinanceEnabled: boolean;
  isInvestorMode: boolean;
}

function describeWorkflows(
  refinanceEnabled: boolean,
  isInvestorMode: boolean
): { headline: string; detail: string; tip: string } {
  const extras: string[] = [];
  if (refinanceEnabled) extras.push('refinance tools');
  if (isInvestorMode) extras.push('investor loan programs');

  const headline =
    extras.length === 0 ? 'Purchase mode' : `Purchase + ${extras.join(' + ')}`;

  const detail =
    extras.length === 0
      ? "You're set up for home buying with property-specific loan estimates."
      : `You still have full purchase workflows; ${extras.join(
          ' and '
        )} are turned on in addition.`;

  const tip =
    extras.length === 0
      ? 'Install the Chrome extension to import listings from any site, pick the fields you want, and jump straight into estimates and offers.'
      : 'You can adjust refinance and investor toggles on your profile anytime. Purchase stays your base experience.';

  return { headline, detail, tip };
}

export const LoginNotificationModal = ({
  open,
  onClose,
  refinanceEnabled,
  isInvestorMode,
}: LoginNotificationModalProps) => {
  const theme = useTheme();
  const isPurchaseOnly = !refinanceEnabled && !isInvestorMode;

  useEffect(() => {
    if (open && !isPurchaseOnly) {
      const timer = setTimeout(() => {
        onClose();
      }, 50000);

      return () => clearTimeout(timer);
    }
  }, [open, onClose, isPurchaseOnly]);

  const { headline, detail, tip } = describeWorkflows(
    refinanceEnabled,
    isInvestorMode
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={isPurchaseOnly ? 'md' : 'sm'}
      fullWidth
      PaperProps={{
        sx: {
          background: theme.palette.mainColors.background,
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.text.primary, 0.2)}`,
          // MUI's 32px margins leave a phone ~260px of dialog.
          m: { xs: 1.5, sm: 4 },
          width: { xs: 'calc(100% - 24px)', sm: undefined },
          maxHeight: { xs: 'calc(100% - 24px)', sm: 'calc(100% - 64px)' },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
          <InfoIcon
            sx={{ color: theme.palette.text.primary, mr: 1, flexShrink: 0 }}
          />
          <Typography
            variant="h6"
            fontWeight={600}
            sx={{ fontSize: { xs: '1.05rem', sm: '1.25rem' }, lineHeight: 1.3 }}
          >
            Welcome to Unbeatable!
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1, px: { xs: 2, sm: 3 } }}>
        <Typography variant="body1" sx={{ mb: 2, color: 'text.primary' }}>
          You&apos;re in <strong>{headline}</strong>. {detail}
        </Typography>

        {isPurchaseOnly ? (
          <PurchaseExtensionOnboardingDemo />
        ) : (
          <Box
            sx={{
              p: 2,
              background: alpha('#0a1f44', 0.05),
              borderRadius: 2,
              border: `1px solid ${alpha('#0a1f44', 0.1)}`,
            }}
          >
            <Typography
              variant="body2"
              sx={{ mb: 1, fontWeight: 500, color: 'text.primary' }}
            >
              Pro tip:
            </Typography>
            <Typography variant="body2" color={theme.palette.text.primary}>
              {tip}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: 2 }}>
        <Button
          onClick={onClose}
          variant="contained"
          color="primary"
          sx={{ minWidth: 100, width: { xs: '100%', sm: 'auto' } }}
        >
          {isPurchaseOnly ? 'Got it — start shopping' : 'Got it!'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
